import { ImsObject } from 'app/shared/collections/ims_object';
import { toUnix } from 'app/shared/utilities/time';
import { TimeSeriesCollection } from 'time-series-collection';
import { AdjustableClosestPastSampleInterpolator } from 'app/shared/collections/time/interpolator/adjustable_closet_past_sample_interpolator';
import { AdjustableProcessor } from 'app/shared/collections/time/processor/AdjustableProcessor';
import { AdjustableInterpolatorWrapper } from 'app/shared/collections/time/interpolator/interpolator';

interface SampleOptions {
    relationshipProp: string;
    startDateProp: string;
}

export class SampleProcessor<T extends ImsObject = ImsObject> implements AdjustableProcessor<T> {
    private readonly _options: SampleOptions;
    private readonly _interpolator: AdjustableInterpolatorWrapper;
    private readonly samples: Map<number, TimeSeriesCollection<T>>;

    constructor(options: SampleOptions, interpolator: AdjustableClosestPastSampleInterpolator) {
        this._options = options;

        // we use a closest past sample interpolator so when retrieving values it will get the closest sample before
        // the requested time (up to a certain limit).
        this._interpolator = interpolator;

        // our internal state
        this.samples = new Map();
    }

    add(items: Array<T>): Promise<void> {
        let lookup: number, col: TimeSeriesCollection<T>, timestamp: number;
        let count = 0;
        items.forEach((stat) => {
            // pull out the foreign key relationship value
            lookup = stat[this._options.relationshipProp];
            // make the collection if need
            if (!this.samples.has(lookup)) {
                this.samples.set(lookup, new TimeSeriesCollection(this._interpolator.interpolator));
            }
            // and retrieve it
            col = this.samples.get(lookup);
            // get the unix timestamp for the stat
            timestamp = toUnix(stat[this._options.startDateProp]);
            // add it to the collection
            ++count;
            col.addSample(timestamp, stat);
        });

        return Promise.resolve(undefined);
    }

    clear(): void {
        this.samples.clear();
    }

    remove(before: number, after: number): Promise<void> {
        for (const col of this.samples.values()) {
            col.removeOutsideTimeFrame(before, after, true);
        }
        return Promise.resolve(undefined);
    }

    get(key: number, time: number): Array<T> {
        // noinspection SuspiciousTypeOfGuard
        if (typeof key === 'string') {
            key = parseInt(key as any);
        }
        const collection = this.samples.get(key);
        if (collection) {
            const value = collection.getValue(time);
            if (value) {
                return [value];
            }
        }
        return [];
    }

    getAllForKey(key: number): Array<T> {
        // make sure the key is really a number
        // noinspection SuspiciousTypeOfGuard
        if (typeof key === 'string') {
            key = parseInt(key as any);
        }
        const collection = this.samples.get(key);
        if (collection) {
            return (collection as any)._state.datums.slice(0);
        }
        return [];
    }

    getAll(): Array<T> {
        const values = [];
        for (const key of this.samples.keys()) {
            values.push(...this.getAllForKey(key));
        }
        return values;
    }

    // used by DynamicStatManager to determine if we need to change the statHoldSeconds
    getStatHoldSeconds(): number {
        return this._interpolator.getStatHoldSeconds();
    }

    // used by DynamicStatManager to change the statHoldSeconds
    setStatHoldSeconds(statHoldSeconds: number): void {
        this._interpolator.setStatHoldSeconds(statHoldSeconds);
    }
}
